import { yupResolver } from '@hookform/resolvers/yup'
import { isEqual } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Icon } from '@/common/components/Icon'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  Input,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import { SearchIcon } from 'lucide-react'
import Loader from '../../../common/components/Loader'
import {
  searchByResponseCheckin,
  searchByResponseTickets,
  searchByResponseTopups,
  searchByResponseTransfer,
} from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { xFestivalName } from '../../../core/constants/baseUrls'
import {
  cancelCheckin,
  cancelDashboardfTicket,
  searchByBarcode,
  searchByEmail,
  searchBySerial,
  syncWristband,
} from '../../../core/services/searchBy'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { getBarcodeColumn } from './barcode-status-table'
import { getCheckinColumn } from './check-in-table'
import { CheckinHistoryModal } from './CheckInHistoryModal'
import { getTicketColumn } from './ticket-table'
import { getTopUpColumn } from './top-up-table'
import { getTransferHistoryColumn } from './transfer-hystory-table'

interface ISupport {
  search: string
}

export const SupportPage: React.FC = () => {
  const [searchBy, setSearchBy] = useState<
    'serial' | 'barcode' | 'email' | 'topupOrder'
  >('email')
  const [ticketColumn, setTicketColumn] = useState<IColumn[]>([])
  const [checkInColumn, setCheckinColumn] = useState<IColumn[]>([])
  const [barcodeColumn, setBarcodeColumn] = useState<IColumn[]>([])
  const [topupsColumn, setTopupsColumn] = useState<IColumn[]>([])
  const [transferHistoryColumn, setTransferHistoryColumn] = useState<IColumn[]>(
    [],
  )
  const [loading, setLoading] = useState<boolean>(false)
  const { festivalName } = useParams()
  const { user } = useAppSelector((s) => s.app)

  const permissions = ![
    'teodora.croitor@untold.com',
    'kinder@gmail.com',
    'ana.caltea@untold.com',
    'sasa@gmail.com',
    'barleamihai@gmail.com',
    'cosmin@gmail.com',
  ].includes(user?.email || '')

  useEffect(() => {
    if (festivalName) {
      xFestivalName.value = festivalName
    }
  }, [festivalName])

  const [showCheckinHistoryModal, setShowCheckinHistoryModal] =
    useState<boolean>(false)
  const [ticketCode, setTicketCode] = useState<string>('')

  const [tickets, setTickets] = useState<searchByResponseTickets[]>([])
  const [checkin, setTCheckin] = useState<searchByResponseCheckin[]>([])
  const [transfers, setTransfers] = useState<searchByResponseTransfer[]>([])
  const [topups, setTopups] = useState<searchByResponseTopups[]>([])

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [showCancelCheckinConfirmation, setShowCancelCheckinConfirmation] =
    useState<boolean>(false)
  const [showCancelTicketConfirmation, setShowCancelTicketConfirmation] =
    useState<boolean>(false)
  const [cancelId, setCancelId] = useState<string>()
  const [cancelTicket, setCancelTicket] = useState<string>()

  const handleUpdateTicket = async (ticketCode: string) => {
    try {
      setLoading(true)
      const resp = await syncWristband(ticketCode)
      if (resp.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Sync Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleCancelTicket = async () => {
    try {
      setLoading(true)
      if (cancelTicket) {
        const resp = await cancelDashboardfTicket(cancelTicket)
        if (resp.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Cancel Successful')
          document.getElementById('support-form-trigger-button')?.click()
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGetCheckinHistory = async (ticketCode: string) => {
    setTicketCode(ticketCode)
    setShowCheckinHistoryModal(true)
  }

  useEffect(() => {
    setLoading(true)
    setTicketColumn(
      getTicketColumn(
        searchBy,
        handleUpdateTicket,
        handleGetCheckinHistory,
        setCancelTicket,
        setShowCancelTicketConfirmation,
        permissions,
      ),
    )
    setCheckinColumn(
      getCheckinColumn(
        searchBy,
        setShowCancelCheckinConfirmation,
        setCancelId,
        handleGetCheckinHistory,
      ),
    )
    setBarcodeColumn(getBarcodeColumn(searchBy))
    setTopupsColumn(getTopUpColumn(searchBy))
    setTransferHistoryColumn(getTransferHistoryColumn(searchBy))
    setLoading(false)
  }, [searchBy])

  const handleSubmitSearch = async (values: ISupport) => {
    try {
      setLoading(true)
      switch (searchBy) {
        case 'serial':
          const responseSerial = await searchBySerial(
            values.search,
            festivalName,
          )
          setTickets(responseSerial.data.tickets)
          setTCheckin(responseSerial.data.checkin)
          setTransfers(responseSerial.data.transfers)
          setTopups(responseSerial.data.topups)
          break
          break
        case 'barcode':
          const responseBarcode = await searchByBarcode(
            values.search,
            festivalName,
          )
          setTickets(responseBarcode.data.tickets)
          setTCheckin(responseBarcode.data.checkin)
          setTopups(responseBarcode.data.topups)
          setTransfers(responseBarcode.data.transfers)
          break
        case 'email':
          const responseEmail = await searchByEmail(values.search, festivalName)
          setTickets(responseEmail.data.tickets)
          setTCheckin(responseEmail.data.checkin)
          setTopups(responseEmail.data.topups)
          setTransfers(responseEmail.data.transfers)
          break
        case 'topupOrder':
          break
        default:
          console.log('should not happen')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const schema = yup.object().shape({
    search: yup
      .string()
      .required('Field is Required')
      .when([], {
        is: () => isEqual(searchBy, 'email'),
        then: (schema) => schema.email('Not a Valid Email!'),
      }),
  })

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
  } = useForm<ISupport>({
    mode: 'onChange',
    resolver: yupResolver<ISupport>(schema),
  })

  const watchSearch = watch('search')

  const cancelFunc = () => {
    // prettier-ignore
    (async () => {
      try {
        if (cancelId) {
          const resp = await cancelCheckin(cancelId)
          setLoading(true)
          switch (searchBy) {
            case 'serial':
              const responseSerial = await searchBySerial(watchSearch)
              setTickets(responseSerial.data.tickets)
              setTCheckin(responseSerial.data.checkin)
              setTransfers(responseSerial.data.transfers)
              setTopups(responseSerial.data.topups)
              break
            case 'barcode':
              const responseBarcode = await searchByBarcode(watchSearch)
              setTickets(responseBarcode.data.tickets)
              setTCheckin(responseBarcode.data.checkin)
              setTransfers(responseBarcode.data.transfers)
              setTopups(responseBarcode.data.topups)
              break
            case 'email':
              const responseEmail = await searchByEmail(watchSearch)
              setTickets(responseEmail.data.tickets)
              setTCheckin(responseEmail.data.checkin)
              setTransfers(responseEmail.data.transfers)
              setTopups(responseEmail.data.topups)
              break
            case 'topupOrder':
              break
            default:
              console.log('should not happen')
          }
          if (resp.status === 200) {
            setIsError(false)
            setShowMessageBox(true)
            setMessage('Cancel Successful')
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  return (
    <>
      <Header pageTitle="Support" />
      {loading && <Loader />}
      <Tabs
        onValueChange={(value) => {
          setSearchBy(value as 'serial' | 'barcode' | 'email' | 'topupOrder')
        }}
        value={searchBy}
      >
        <div className="max-h-[calc(100vh-118px)] overflow-y-auto space-y-4">
          <div className="flex flex-col gap-3">
            <TabsList className="w-full">
              <div className="bg-white/10 p-2.5 rounded-[20px]">
                <TabsTrigger value="serial" className="gap-1">
                  <span>Search by</span>{' '}
                  <span className="font-bold">Serial Number</span>
                </TabsTrigger>
                <TabsTrigger value="barcode" className="gap-1">
                  <span>Search by</span>{' '}
                  <span className="font-bold">Barcode</span>
                </TabsTrigger>
                <TabsTrigger value="topupOrder" className="gap-1">
                  <span>Search by</span>{' '}
                  <span className="font-bold">Top Up Order</span>
                </TabsTrigger>
                <TabsTrigger value="email" className="gap-1">
                  <span>Search by</span>{' '}
                  <span className="font-bold">Email</span>
                </TabsTrigger>
                <form
                  id="support-form"
                  className="relative mt-[10px]"
                  onSubmit={handleSubmit(handleSubmitSearch)}
                >
                  <div className="flex gap-2 items-center">
                    <Input
                      isInvalid={!!errors?.search?.message}
                      {...register('search')}
                      type="text"
                      placeholder="Search ..."
                      className="p-3 w-full !bg-monochrome-4-60 h-[64px] rounded-[50px]"
                      prefix={
                        <Button
                          type="submit"
                          form="support-form"
                          variant="ghost"
                          id="support-form-trigger-button"
                          size="icon"
                        >
                          <SearchIcon className="w-4 h-4" />
                        </Button>
                      }
                    />
                  </div>
                </form>
              </div>
            </TabsList>
          </div>
          <div className="flex w-full justify-center gap-4 p-3 bg-monochrome-120 rounded-2xl">
            <div className="flex gap-2 items-center">
              <Icon
                icon="check"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                className="text-accent-1"
                fill="none"
              />
              <span className="text-monochrome-1 text-body-medium">
                Sincronizat cu success
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <Icon
                icon="close-circle-outline"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                className="text-accent-3"
                fill="none"
              />
              <span className="text-monochrome-1 text-body-medium">
                Nesincronizat
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <Icon
                icon="close-circle-outline"
                width="18"
                height="18"
                viewBox="0 0 18 18"
                className="text-accent-3"
                fill="none"
              />
              <span className="text-monochrome-1 text-body-medium">
                Eroare de la sincronizare (raporteaza situatia)
              </span>
            </div>
            <div className="flex gap-2 items-center">
              <span className="text-accent-3 text-body-medium">COD101</span>
              <span className="text-monochrome-1 text-body-medium">
                Marcat ca frauda (contacteaza Oana Onicas sau Dutu - Resolution)
              </span>
            </div>
          </div>

          <Card className="bg-gradient-4 p-7.5 space-y-4">
            <PageTitle title="Tickets" />
            <Table
              columns={ticketColumn}
              data={tickets}
              maxHeight="auto"
              className="[&_th]:!whitespace-nowrap"
            />
          </Card>

          <Card className="bg-gradient-4 p-7.5 space-y-4">
            <PageTitle title="Online Check-in" icon="online-check-in" />
            <Table
              columns={checkInColumn}
              data={checkin}
              maxHeight="auto"
              className="[&_th]:!whitespace-nowrap"
            />
          </Card>

          {/* <div className="flex flex-col gap-4">
          <h4>Check-in by Barcode Status</h4>
          <Table
            table-hover
            className="bg-white"
            columns={barcodeColumn}
            data={[]}
          />
        </div> */}
          <Card className="bg-gradient-4 p-7.5 space-y-4">
            <PageTitle title="Transfer History" icon="transfer-history" />
            <Table
              columnWidth="25%"
              columns={transferHistoryColumn}
              data={transfers}
              maxHeight="auto"
              className="[&_th]:!whitespace-nowrap"
            />
          </Card>
          <Card className="bg-gradient-4 p-7.5 space-y-4">
            <PageTitle title="Top-ups" icon="topup-coin" />
            <Table
              columns={topupsColumn}
              data={topups}
              maxHeight="auto"
              className="[&_th]:!whitespace-nowrap"
            />
          </Card>
          <CheckinHistoryModal
            ticketCode={ticketCode}
            showCheckinHistoryModal={showCheckinHistoryModal}
            setShowCheckinHistoryModal={setShowCheckinHistoryModal}
          />
          <DeleteConfirmationModal
            showDeleteConfirmationModal={showCancelCheckinConfirmation}
            setDeleteConfirmationModal={setShowCancelCheckinConfirmation}
            deleteTitle={'Checkin'}
            deleteFunc={cancelFunc}
            cancelText={true}
          />
          <DeleteConfirmationModal
            showDeleteConfirmationModal={showCancelTicketConfirmation}
            setDeleteConfirmationModal={setShowCancelTicketConfirmation}
            deleteTitle={'Ticket'}
            deleteFunc={handleCancelTicket}
            cancelText={true}
          />
          <MessageBox
            message={message}
            setShowMessageBox={setShowMessageBox}
            showMessageBox={showMessageBox}
            isError={isError}
            errorStatus={errorStatus}
          />
        </div>
      </Tabs>
    </>
  )
}
